/**
 * Hook for Conferência Verificação list page.
 * Loads all conferências from new API and provides frontend filtering.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  listarConferenciasVerificacao,
  criarConferencia,
  criarConferenciaAvulsa,
  ConferenciaVerificacao,
} from '../services/conferenciaService';
import { toast } from 'react-hot-toast';
import type { FilterOption } from '../components/MultiSelectFilter';

export function useConferenciaList() {
  const [allConferencias, setAllConferencias] = useState<ConferenciaVerificacao[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientes, setSelectedClientes] = useState<string[]>([]);
  const [selectedHospitals, setSelectedHospitals] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Load data
  const loadConferencias = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listarConferenciasVerificacao();
      setAllConferencias(data);
    } catch {
      // toast already shown in service
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConferencias();
  }, [loadConferencias]);

  // Filter options (MultiSelectFilter espera FilterOption[] = {value,label})
  const clienteOptions = useMemo<FilterOption[]>(() => {
    const names = new Set<string>();
    allConferencias.forEach(c => {
      if (c.cliente_nome) names.add(c.cliente_nome);
    });
    return Array.from(names).sort().map(n => ({ value: n, label: n }));
  }, [allConferencias]);

  const hospitalOptions = useMemo<FilterOption[]>(() => {
    const names = new Set<string>();
    allConferencias.forEach(c => {
      if (c.hospital_nome) names.add(c.hospital_nome);
    });
    return Array.from(names).sort().map(n => ({ value: n, label: n }));
  }, [allConferencias]);

  const statusOptions = useMemo<FilterOption[]>(() => [
    { value: 'PENDENTE', label: 'Pendente' },
    { value: 'FINALIZADA', label: 'Finalizada' },
  ], []);

  // Apply filters
  const conferencias = useMemo(() => {
    let result = allConferencias;

    // Status filter
    if (selectedStatus.length > 0 && !selectedStatus.includes('TODOS')) {
      result = result.filter(c => {
        if (selectedStatus.includes('PENDENTE') && !c.finalizada) return true;
        if (selectedStatus.includes('FINALIZADA') && c.finalizada) return true;
        return false;
      });
    }

    // Cliente filter
    if (selectedClientes.length > 0) {
      result = result.filter(c => c.cliente_nome && selectedClientes.includes(c.cliente_nome));
    }

    // Hospital filter
    if (selectedHospitals.length > 0) {
      result = result.filter(c => c.hospital_nome && selectedHospitals.includes(c.hospital_nome));
    }

    // Search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        (c.numero_conferencia?.toString() || '').includes(term) ||
        (c.cliente_nome || '').toLowerCase().includes(term) ||
        (c.hospital_nome || '').toLowerCase().includes(term) ||
        (c.lacres || '').toLowerCase().includes(term) ||
        (c.ordem_coleta || '').toLowerCase().includes(term) ||
        (c.contagem_titulo || '').toLowerCase().includes(term) ||
        (c.job_id?.toString() || '').includes(term)
      );
    }

    return result;
  }, [allConferencias, selectedStatus, selectedClientes, selectedHospitals, searchTerm]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedClientes.length > 0) count++;
    if (selectedHospitals.length > 0) count++;
    if (selectedStatus.length > 0) count++;
    return count;
  }, [selectedClientes, selectedHospitals, selectedStatus]);

  // Actions
  const handleCreateConferencia = useCallback(async (jobId: number, clienteId?: number) => {
    try {
      const result = await criarConferencia(jobId, clienteId);
      if (result.success) {
        toast.success(result.message);
        await loadConferencias();
        return result.data.id;
      }
    } catch {
      // toast in service
    }
    return null;
  }, [loadConferencias]);

  const handleCreateAvulsa = useCallback(async (clienteId: number, hospitalId: number) => {
    try {
      const result = await criarConferenciaAvulsa(clienteId, hospitalId);
      if (result.success) {
        toast.success(result.message);
        await loadConferencias();
        return result.data.id;
      }
    } catch {
      // toast in service
    }
    return null;
  }, [loadConferencias]);

  const handleClearFilters = useCallback(() => {
    setSelectedClientes([]);
    setSelectedHospitals([]);
    setSelectedStatus([]);
    setSearchTerm('');
  }, []);

  return {
    conferencias,
    allConferencias,
    loading,
    searchTerm,
    setSearchTerm,
    selectedClientes,
    setSelectedClientes,
    selectedHospitals,
    setSelectedHospitals,
    selectedStatus,
    setSelectedStatus,
    showFilters,
    setShowFilters,
    clienteOptions,
    hospitalOptions,
    statusOptions,
    activeFilterCount,
    handleClearFilters,
    handleCreateConferencia,
    handleCreateAvulsa,
    loadConferencias,
  };
}
